// servizi/nativo.ts — v030: la «sensazione nativa» nelle app Android e iOS (richiesta del proprietario: Apple non
// approva un'app che sembra un sito). Con Capacitor: vibrazioni del sistema (Haptics), foglio di condivisione nativo
// (Share), barra di stato scura sopra l'app (StatusBar), schermata d'avvio che sparisce quando l'app è pronta
// (SplashScreen), tastiera che non copre i campi (Keyboard) e tasto «indietro» di Android (App). Nel browser si usa
// quello che il browser ha (navigator.vibrate, navigator.share) o niente. I plugin si caricano solo nelle app.
import { PIATTAFORMA } from "../costanti.ts";
import { leggiPreferenza } from "./preferenze.ts";
import { PREFERENZE } from "../costanti.ts";

export const NATIVA = PIATTAFORMA === "android" || PIATTAFORMA === "ios";

export type Tocco = "leggero" | "medio" | "forte" | "successo" | "errore" | "scelta";

const riduci = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
/** Le vibrazioni seguono i suoni del gioco: con i suoni spenti non vibra niente. */
const attive = () => leggiPreferenza(PREFERENZE.suoni) !== "no";

/** Una vibrazione breve, come nelle app native (pulsanti importanti, risultato, acquisto). */
export function vibra(t: Tocco = "leggero"): void {
  if (!attive()) return;
  if (NATIVA) {
    void import("@capacitor/haptics")
      .then(({ Haptics, ImpactStyle, NotificationType }) => {
        if (t === "successo") return Haptics.notification({ type: NotificationType.Success });
        if (t === "errore") return Haptics.notification({ type: NotificationType.Error });
        if (t === "scelta") return Haptics.selectionChanged();
        return Haptics.impact({ style: t === "forte" ? ImpactStyle.Heavy : t === "medio" ? ImpactStyle.Medium : ImpactStyle.Light });
      })
      .catch(() => undefined);
    return;
  }
  if (riduci()) return;
  const ms = { leggero: 8, medio: 15, forte: 30, successo: [12, 60, 24], errore: [30, 40, 30], scelta: 5 }[t];
  try {
    navigator.vibrate?.(ms);
  } catch {
    // alcuni browser non vogliono vibrare senza un gesto: niente
  }
}

/**
 * Condividere: nelle app il foglio nativo del sistema, nel browser navigator.share. Restituisce false se non si è
 * potuto (chi chiama copia il link).
 */
export async function condividi(d: { titolo: string; testo: string; url?: string }): Promise<boolean> {
  if (NATIVA) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title: d.titolo, text: d.testo, url: d.url, dialogTitle: d.titolo });
      return true;
    } catch (e) {
      // annullato dall'utente: va bene così
      return /cancel/i.test(String((e as Error)?.message ?? e));
    }
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title: d.titolo, text: d.testo, url: d.url });
      return true;
    } catch (e) {
      return (e as Error)?.name === "AbortError";
    }
  }
  return false;
}

/** All'avvio delle app: barra di stato, schermata d'avvio, tastiera, tasto indietro. Nel browser non fa niente. */
export async function avviaNativo(): Promise<void> {
  if (!NATIVA) return;
  document.documentElement.classList.add("nativa", `nativa-${PIATTAFORMA}`);
  // v046: come nelle app vere, i pulsanti principali «si sentono» sotto il dito (un tocco leggero del motore)
  document.addEventListener(
    "pointerdown",
    (e) => {
      const el = (e.target as Element | null)?.closest?.(".ld-cta, .pulsante-primario, .ld-riquadro, .ld-fine-azione, .ld-pillola");
      if (el && !(el as HTMLButtonElement).disabled) vibra("leggero");
    },
    { passive: true, capture: true },
  );
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (PIATTAFORMA === "android") {
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.setBackgroundColor({ color: "#00000000" });
    }
  } catch {
    // plugin assente in una build vecchia: niente
  }
  try {
    const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    if (PIATTAFORMA === "ios") await Keyboard.setAccessoryBarVisible({ isVisible: true });
  } catch {
    // niente
  }
  try {
    const { App } = await import("@capacitor/app");
    // Android: «indietro» chiude prima una finestra aperta, poi torna alla pagina di prima, dalla Home esce dall'app
    await App.addListener("backButton", ({ canGoBack }) => {
      const finestra = document.querySelector<HTMLDialogElement>("dialog[open]");
      if (finestra) {
        finestra.close();
        return;
      }
      const sovrapposta = document.querySelector<HTMLElement>(".ld-tutorial, .ld-anteprima, .ld-guida, .ld-mascotte-sfondo");
      if (sovrapposta) {
        sovrapposta.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
        return;
      }
      if (canGoBack && location.pathname !== "/") history.back();
      else void App.exitApp();
    });
  } catch {
    // niente
  }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch {
    // niente
  }
}
